import type { GameReviewsUseQueryParams } from './review.schema';
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueriesKeys } from '../queriesKeys';
import { reviewApi } from './review.api';

export const GAME_REVIEWS_STALE_TIME = 5 * 60 * 1000;

export const reviewsQueryOptions = {
  getByGame: (args: GameReviewsUseQueryParams) => queryOptions({
    queryKey: [QueriesKeys.gameReviews, args.gameId],
    queryFn: () => reviewApi.getReviewsByGame(args.gameId, args.params),
    staleTime: GAME_REVIEWS_STALE_TIME,
    ...args.options,
    enabled: !!args.gameId,
  }),

  getByGameInfinite: (args: GameReviewsUseQueryParams) => {
    if (!args.gameId) {
      throw new Error('UseInfinityQuery: gameId is required for infinite query');
    }

    return infiniteQueryOptions({
      queryKey: [QueriesKeys.gameReviews, args.gameId, 'infinite'],
      queryFn: async ({ pageParam }) => {
        return await reviewApi.getReviewsByGame(args.gameId, { ...args.params, page: pageParam });
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (!lastPage || !lastPage.hasNextPage) {
          return undefined;
        }
        return lastPage.page + 1;
      },
      staleTime: GAME_REVIEWS_STALE_TIME,
    });
  },
};

/** Mutation */
export const useCreateGameReviewMutation = (gameId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { username?: string; comment: string; rating: number }) =>
      reviewApi.addReviewForGame(gameId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueriesKeys.gameReviews, gameId] });
    },
  });
};
